import { useEffect, useState } from "react";
import { IPromoCode } from "../models/IPromoCode";
import { getPromoCodeById } from "../services/order";

interface Props {
  promoCodeId: string | null;
  onClose: () => void;
}

export function PromoCodePopup({ promoCodeId, onClose }: Props) {
  const [data, setData]       = useState<IPromoCode | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!promoCodeId) { setData(null); return; }
    setLoading(true);
    getPromoCodeById(promoCodeId)
      .then(res => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [promoCodeId]);

  if (!promoCodeId) return null;

  const remaining = data ? data.maxUseCount - data.currentUseCount : 0;
  const usePct    = data ? Math.round((data.currentUseCount / data.maxUseCount) * 100) : 0;
  const expiry    = data ? new Date(data.expiryDate).toLocaleDateString("tr-TR", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";

  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1100, display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(3px)" }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ background: "#fff", borderRadius: 20, width: "min(96vw, 440px)", boxShadow: "0 8px 40px rgba(0,0,0,0.2)", overflow: "hidden" }}>

        {/* Header */}
        <div style={{ background: "linear-gradient(135deg, #7c3aed, #6d28d9)", padding: "20px 22px 18px", color: "#fff" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 38, height: 38, borderRadius: 11, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <i className="bi bi-tag-fill" style={{ fontSize: "1.1rem" }}></i>
              </div>
              <div>
                <div style={{ fontSize: "0.7rem", opacity: 0.75, textTransform: "uppercase", letterSpacing: "0.5px" }}>Promosyon Kodu</div>
                <div style={{ fontSize: "1.2rem", fontWeight: 900, letterSpacing: "2px" }}>
                  {loading ? "..." : data?.code ?? "—"}
                </div>
              </div>
            </div>
            <button onClick={onClose} style={{ background: "rgba(255,255,255,0.15)", border: "none", borderRadius: 8, color: "#fff", cursor: "pointer", width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1rem" }}>
              <i className="bi bi-x-lg"></i>
            </button>
          </div>

          {!loading && data && (
            <div style={{ marginTop: 14, display: "flex", alignItems: "center", gap: 8 }}>
              <div style={{ background: "rgba(255,255,255,0.95)", borderRadius: 10, padding: "6px 14px", display: "inline-flex", alignItems: "center", gap: 6 }}>
                <i className="bi bi-percent" style={{ color: "#7c3aed", fontSize: "1rem" }}></i>
                <span style={{ fontWeight: 900, fontSize: "1.3rem", color: "#7c3aed" }}>
                  {data.discountType === "Percentage" ? `%${data.discountValue}` : `${data.discountValue} TL`}
                </span>
                <span style={{ fontSize: "0.78rem", color: "#6d28d9", fontWeight: 600 }}>
                  {data.discountType === "Percentage" ? "indirim" : "sabit indirim"}
                </span>
              </div>
              <div style={{ fontSize: "0.75rem", opacity: 0.85 }}>{data.description}</div>
            </div>
          )}
        </div>

        {loading ? (
          <div style={{ padding: "40px", textAlign: "center" }}>
            <div className="spinner-border text-primary" role="status" />
          </div>
        ) : data ? (
          <div style={{ padding: "18px 22px 22px", display: "flex", flexDirection: "column", gap: 14 }}>

            {/* Kullanım bar */}
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                <span style={{ fontSize: "0.78rem", fontWeight: 600, color: "#374151" }}>
                  <i className="bi bi-people-fill me-1 text-purple" style={{ color: "#7c3aed" }}></i>Kullanım
                </span>
                <span style={{ fontSize: "0.78rem", color: "#6b7280" }}>
                  {data.currentUseCount} / {data.maxUseCount}
                </span>
              </div>
              <div style={{ background: "#f3f4f6", borderRadius: 8, height: 8, overflow: "hidden" }}>
                <div style={{ width: `${usePct}%`, height: "100%", background: usePct >= 100 ? "#ef4444" : usePct >= 80 ? "#f59e0b" : "#7c3aed", borderRadius: 8, transition: "width 0.4s" }} />
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4 }}>
                <span style={{ fontSize: "0.7rem", color: "#9ca3af" }}>Kullanıldı: {data.currentUseCount}</span>
                <span style={{ fontSize: "0.7rem", color: remaining === 0 ? "#ef4444" : "#059669", fontWeight: 700 }}>
                  {remaining === 0 ? "Tükendi" : `${remaining} kullanım kaldı`}
                </span>
              </div>
            </div>

            {/* Detay grid */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              {[
                { icon: "bi-person-check", label: "Kişi başı limit", value: `${data.maxUsePerCustomer} kez` },
                { icon: "bi-cart-check", label: "Min. sipariş", value: data.minOrderAmount > 0 ? `${data.minOrderAmount} TL` : "Yok" },
                { icon: "bi-calendar-x", label: "Son kullanım", value: expiry, full: true },
              ].map(item => (
                <div key={item.label}
                  style={{ gridColumn: item.full ? "1 / -1" : undefined, background: "#f9fafb", borderRadius: 10, padding: "10px 12px", border: "1px solid #f1f5f9" }}
                >
                  <div style={{ fontSize: "0.68rem", color: "#9ca3af", marginBottom: 3 }}>
                    <i className={`bi ${item.icon} me-1`}></i>{item.label}
                  </div>
                  <div style={{ fontSize: "0.88rem", fontWeight: 700, color: "#111827" }}>{item.value}</div>
                </div>
              ))}
            </div>

            {/* Durum */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, padding: "8px", borderRadius: 10, background: data.isActive && remaining > 0 ? "#f0fdf4" : "#fef2f2", border: `1px solid ${data.isActive && remaining > 0 ? "#bbf7d0" : "#fecaca"}` }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: data.isActive && remaining > 0 ? "#10b981" : "#ef4444", flexShrink: 0 }} />
              <span style={{ fontSize: "0.82rem", fontWeight: 700, color: data.isActive && remaining > 0 ? "#059669" : "#dc2626" }}>
                {!data.isActive ? "Pasif" : remaining === 0 ? "Kota doldu" : "Aktif & Kullanılabilir"}
              </span>
            </div>
          </div>
        ) : (
          <div style={{ padding: "30px", textAlign: "center", color: "#9ca3af", fontSize: "0.88rem" }}>
            Promo kodu bilgisi yüklenemedi.
          </div>
        )}
      </div>
    </div>
  );
}
